// Super Admin OHT governance API client.
// Platform-wide: do not send x-acceptingmd-oht-id.

export type NavigatorIntakeReadiness = {
  hasCoverage: boolean;
  hasActiveNavigator: boolean;
  canEnable: boolean;
};

export type SuperAdminOhtSummary = {
  ohtId: number;
  name: string | null;
  code: string | null;
  isActive: boolean;
  isNavigatorIntakeEnabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  coverageFsaCount: number;
  clinicCount: number;
  doctorCount: number;
  activeOhtAdminCount: number;
  activeCommunityNavigatorCount: number;
  navigatorIntakeReadiness: NavigatorIntakeReadiness;
};

export type SuperAdminOhtCoverageFsa = {
  fsa: string;
  createdAt: string | null;
};

export type SuperAdminOhtDetail = SuperAdminOhtSummary & {
  coverageFsas: SuperAdminOhtCoverageFsa[];
};

export type SuperAdminOhtsListResponse = {
  ohts?: SuperAdminOhtSummary[];
  error?: string;
};

export type SuperAdminOhtDetailResponse = {
  oht?: SuperAdminOhtDetail | null;
  error?: string;
};

export type CreateSuperAdminOhtInput = {
  name: string;
  code: string;
  isActive?: boolean;
};

export type UpdateSuperAdminOhtInput = {
  name?: string;
  code?: string;
  isActive?: boolean;
};

export class SuperAdminOhtApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "SuperAdminOhtApiError";
    this.status = status;
    this.body = body;
  }
}

/** Active OHTs missing coverage, admins, or navigators when intake is on. */
export function ohtNeedsAttention(oht: SuperAdminOhtSummary): boolean {
  if (!oht.isActive) return false;
  if (oht.coverageFsaCount === 0) return true;
  if (oht.activeOhtAdminCount === 0) return true;
  if (oht.isNavigatorIntakeEnabled && oht.activeCommunityNavigatorCount === 0) {
    return true;
  }
  return false;
}

export function readinessMissingLabels(
  oht: Pick<SuperAdminOhtSummary, "isActive" | "navigatorIntakeReadiness">
): string[] {
  const readiness = oht.navigatorIntakeReadiness || {
    hasCoverage: false,
    hasActiveNavigator: false,
    canEnable: false,
  };
  const missing: string[] = [];
  if (!oht.isActive) missing.push("OHT inactive");
  if (!readiness.hasCoverage) missing.push("No FSA coverage");
  if (!readiness.hasActiveNavigator) missing.push("No active Community Navigator");
  return missing;
}

export const OHT_NAME_MAX = 255;
export const OHT_CODE_MAX = 50;

export function validateOhtName(raw: string): string | null {
  const name = String(raw ?? "").trim();
  if (!name) return "OHT name is required.";
  if (name.length > OHT_NAME_MAX) {
    return `OHT name must be at most ${OHT_NAME_MAX} characters.`;
  }
  return null;
}

export function validateOhtCode(raw: string): string | null {
  const code = String(raw ?? "").trim();
  if (!code) return "OHT code is required.";
  if (code.length > OHT_CODE_MAX) {
    return `OHT code must be at most ${OHT_CODE_MAX} characters.`;
  }
  return null;
}

async function parseJsonSafe(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function errorMessage(body: any, fallback: string): string {
  if (body && typeof body === "object" && typeof body.error === "string") {
    const msg = body.error.trim();
    if (msg) return msg;
  }
  return fallback;
}

function throwOhtApiError(res: Response, body: any, fallback: string): never {
  if (res.status === 404) {
    throw new SuperAdminOhtApiError(
      404,
      "Ontario Health Team not found.",
      body
    );
  }

  if (res.status === 409) {
    throw new SuperAdminOhtApiError(
      409,
      errorMessage(
        body,
        "An Ontario Health Team with this code already exists."
      ),
      body
    );
  }

  throw new SuperAdminOhtApiError(
    res.status,
    errorMessage(body, fallback),
    body
  );
}

export async function listSuperAdminOhts(
  signal?: AbortSignal
): Promise<SuperAdminOhtSummary[]> {
  const res = await fetch("/api/super-admin/ohts", {
    method: "GET",
    credentials: "include",
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtsListResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to load OHTs (${res.status})`);
  }

  return Array.isArray(body?.ohts) ? body!.ohts! : [];
}

export async function getSuperAdminOht(
  ohtId: string | number,
  signal?: AbortSignal
): Promise<SuperAdminOhtDetail> {
  const id = encodeURIComponent(String(ohtId).trim());
  const res = await fetch(`/api/super-admin/ohts/${id}`, {
    method: "GET",
    credentials: "include",
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtDetailResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to load OHT (${res.status})`);
  }

  if (!body?.oht) {
    throw new SuperAdminOhtApiError(
      404,
      "Ontario Health Team not found.",
      body
    );
  }

  return body.oht;
}

export async function createSuperAdminOht(
  input: CreateSuperAdminOhtInput,
  signal?: AbortSignal
): Promise<SuperAdminOhtDetail> {
  const payload: CreateSuperAdminOhtInput = {
    name: String(input.name ?? "").trim(),
    code: String(input.code ?? "").trim(),
  };
  if (typeof input.isActive === "boolean") {
    payload.isActive = input.isActive;
  }

  const res = await fetch("/api/super-admin/ohts", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtDetailResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to create OHT (${res.status})`);
  }

  if (!body?.oht) {
    throw new SuperAdminOhtApiError(
      500,
      "Created OHT response was incomplete.",
      body
    );
  }

  return body.oht;
}

export async function updateSuperAdminOht(
  ohtId: string | number,
  input: UpdateSuperAdminOhtInput,
  signal?: AbortSignal
): Promise<SuperAdminOhtDetail> {
  const id = encodeURIComponent(String(ohtId).trim());
  const payload: UpdateSuperAdminOhtInput = {};

  if (input.name !== undefined) {
    payload.name = String(input.name).trim();
  }
  if (input.code !== undefined) {
    payload.code = String(input.code).trim();
  }
  if (typeof input.isActive === "boolean") {
    payload.isActive = input.isActive;
  }

  const res = await fetch(`/api/super-admin/ohts/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtDetailResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to update OHT (${res.status})`);
  }

  if (!body?.oht) {
    throw new SuperAdminOhtApiError(
      500,
      "Updated OHT response was incomplete.",
      body
    );
  }

  return body.oht;
}

export type NavigatorIntakePrerequisiteReadiness = {
  ohtActive?: boolean;
  hasCoverage?: boolean;
  hasActiveNavigator?: boolean;
};

/**
 * Map structured 409 readiness flags to user-facing prerequisite messages.
 */
export function formatNavigatorIntakePrerequisiteMessages(
  readiness: NavigatorIntakePrerequisiteReadiness | null | undefined
): string[] {
  if (!readiness || typeof readiness !== "object") return [];
  const messages: string[] = [];
  if (readiness.ohtActive === false) {
    messages.push("OHT must be active.");
  }
  if (readiness.hasCoverage === false) {
    messages.push("At least one coverage FSA is required.");
  }
  if (readiness.hasActiveNavigator === false) {
    messages.push("At least one active Community Navigator is required.");
  }
  return messages;
}

export async function updateSuperAdminOhtNavigatorIntake(
  ohtId: string | number,
  enabled: boolean,
  signal?: AbortSignal
): Promise<SuperAdminOhtDetail> {
  if (typeof enabled !== "boolean") {
    throw new SuperAdminOhtApiError(400, "enabled must be a boolean");
  }

  const id = encodeURIComponent(String(ohtId).trim());
  const res = await fetch(`/api/super-admin/ohts/${id}/navigator-intake`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
    signal,
  });

  const body = (await parseJsonSafe(res)) as
    | (SuperAdminOhtDetailResponse & {
        readiness?: NavigatorIntakePrerequisiteReadiness;
      })
    | null;

  if (!res.ok) {
    throwOhtApiError(
      res,
      body,
      `Failed to update Navigator intake (${res.status})`
    );
  }

  if (!body?.oht) {
    throw new SuperAdminOhtApiError(
      500,
      "Navigator intake update response was incomplete.",
      body
    );
  }

  return body.oht;
}

/**
 * Canonical Canadian FSA: trim, uppercase, exactly 3 chars, LETTER DIGIT LETTER.
 * Mirrors backend normalizeCanadianFsa (rejects full postal codes).
 */
export function normalizeCanadianFsa(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string" && typeof value !== "number") return null;

  const trimmed = String(value).trim().toUpperCase();
  if (!/^[A-Z]\d[A-Z]$/.test(trimmed)) return null;
  return trimmed;
}

export function validateCanadianFsaInput(
  raw: unknown,
  existingFsas: string[] = []
): string | null {
  const text = String(raw ?? "").trim();
  if (!text) return "Enter an FSA (for example L5L).";

  const normalized = normalizeCanadianFsa(raw);
  if (!normalized) {
    return "Enter a valid Canadian FSA (letter-digit-letter, for example L5L).";
  }

  const existing = new Set(
    existingFsas.map((f) => String(f).trim().toUpperCase()).filter(Boolean)
  );
  if (existing.has(normalized)) {
    return `${normalized} is already in this coverage set.`;
  }

  return null;
}

export function sortFsas(fsas: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of fsas) {
    const fsa = String(raw).trim().toUpperCase();
    if (!fsa || seen.has(fsa)) continue;
    seen.add(fsa);
    out.push(fsa);
  }
  return out.sort();
}

export function coverageSetsEqual(a: string[], b: string[]): boolean {
  const left = sortFsas(a);
  const right = sortFsas(b);
  if (left.length !== right.length) return false;
  return left.every((fsa, i) => fsa === right[i]);
}

export function coverageDiff(saved: string[], desired: string[]) {
  const savedSet = new Set(sortFsas(saved));
  const desiredSorted = sortFsas(desired);
  const desiredSet = new Set(desiredSorted);
  return {
    toAdd: desiredSorted.filter((f) => !savedSet.has(f)),
    toRemove: sortFsas(saved).filter((f) => !desiredSet.has(f)),
  };
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v) => typeof v === "string" || typeof v === "number")
    .map((v) => String(v).trim().toUpperCase())
    .filter(Boolean);
}

function asCaseConflictList(
  value: unknown
): Array<{ fsa: string; activeCaseCount: number }> {
  if (!Array.isArray(value)) return [];
  const rows: Array<{ fsa: string; activeCaseCount: number }> = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const fsa = String((item as any).fsa ?? "").trim().toUpperCase();
    if (!fsa) continue;
    const count = Number((item as any).activeCaseCount);
    rows.push({
      fsa,
      activeCaseCount: Number.isFinite(count) ? count : 0,
    });
  }
  return rows;
}

function ownershipMessages(fsas: string[]): string[] {
  if (!fsas.length) return [];
  if (fsas.length === 1) {
    return [
      `${fsas[0]} is already assigned to another Ontario Health Team.`,
    ];
  }
  return [
    `Some FSAs are already assigned to another Ontario Health Team: ${fsas.join(", ")}.`,
  ];
}

function targetMessages(fsas: string[]): string[] {
  return fsas.map(
    (fsa) =>
      `${fsa} cannot be removed because an OHT target still references it.`
  );
}

function caseMessages(
  rows: Array<{ fsa: string; activeCaseCount: number }>
): string[] {
  return rows.map((row) => {
    const n = row.activeCaseCount;
    const label = n === 1 ? "case" : "cases";
    return `${row.fsa} cannot be removed because ${n} active Community Navigator ${label} still depend on it.`;
  });
}

/**
 * Map coverage PUT 409 body into safe, user-facing messages (no PHI).
 */
export function formatCoverageConflictMessages(body: unknown): string[] {
  const payload =
    body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  const errorText =
    typeof payload.error === "string" ? payload.error.trim() : "";

  const ownership = asStringList(payload.ownershipConflicts);
  const targets = asStringList(payload.targetConflicts);
  const cases = asCaseConflictList(payload.caseConflicts);

  const messages: string[] = [];
  messages.push(...ownershipMessages(ownership));
  messages.push(...caseMessages(cases));
  messages.push(...targetMessages(targets));

  if (messages.length) return messages;

  const conflicts = payload.conflicts;
  if (Array.isArray(conflicts) && conflicts.length) {
    if (
      conflicts.every(
        (c) => c && typeof c === "object" && typeof (c as any).fsa === "string"
      )
    ) {
      return caseMessages(asCaseConflictList(conflicts));
    }

    const fsas = asStringList(conflicts);
    if (/target/i.test(errorText)) {
      return targetMessages(fsas);
    }
    return ownershipMessages(fsas);
  }

  if (errorText) return [errorText];
  return ["Coverage could not be updated."];
}

export async function updateSuperAdminOhtCoverage(
  ohtId: string | number,
  fsas: string[],
  signal?: AbortSignal
): Promise<SuperAdminOhtDetail> {
  const id = encodeURIComponent(String(ohtId).trim());
  const payload = {
    fsas: sortFsas(fsas),
  };

  const res = await fetch(`/api/super-admin/ohts/${id}/coverage`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtDetailResponse | null;

  if (!res.ok) {
    if (res.status === 409) {
      const messages = formatCoverageConflictMessages(body);
      throw new SuperAdminOhtApiError(
        409,
        messages.join(" ") ||
          errorMessage(body, "Coverage could not be updated."),
        body
      );
    }
    throwOhtApiError(res, body, `Failed to update coverage (${res.status})`);
  }

  if (!body?.oht) {
    throw new SuperAdminOhtApiError(
      500,
      "Updated coverage response was incomplete.",
      body
    );
  }

  return body.oht;
}

export type SuperAdminOhtMemberRole = "OHTAdmin" | "CommunityNavigator";

export type SuperAdminOhtMember = {
  userRoleId: number;
  userId: number;
  displayName: string | null;
  email: string | null;
  userStatus: string | null;
  roleName: SuperAdminOhtMemberRole | string;
  isActive: boolean;
  activatedAt: string | null;
  deactivatedAt: string | null;
};

export type SuperAdminOhtMemberAssignmentState =
  | "created"
  | "reactivated"
  | "already_active"
  | string;

export type CreateSuperAdminOhtMemberInput = {
  displayName: string;
  email: string;
  roleName: SuperAdminOhtMemberRole;
};

export type SuperAdminOhtNotificationStatus = {
  attempted: boolean;
  sent: boolean;
};

export type CreateSuperAdminOhtMemberResult = {
  member: SuperAdminOhtMember;
  userCreated: boolean;
  assignmentState: SuperAdminOhtMemberAssignmentState;
  alreadyAssigned: boolean;
  notification: SuperAdminOhtNotificationStatus;
  warning: string | null;
};

export type ReactivateSuperAdminOhtMemberResult = {
  member: SuperAdminOhtMember;
  alreadyActive: boolean;
  notification: SuperAdminOhtNotificationStatus;
  warning: string | null;
};

export type ResendSuperAdminOhtMemberAccessEmailResult = {
  notification: SuperAdminOhtNotificationStatus;
};

export type SuperAdminOhtMembersListResponse = {
  members?: SuperAdminOhtMember[];
  error?: string;
};

export type SuperAdminOhtMemberMutationResponse = {
  member?: SuperAdminOhtMember | null;
  userCreated?: boolean;
  assignmentState?: SuperAdminOhtMemberAssignmentState;
  alreadyAssigned?: boolean;
  alreadyInactive?: boolean;
  alreadyActive?: boolean;
  navigatorIntakeForcedOff?: boolean;
  notification?: {
    attempted?: boolean;
    sent?: boolean;
  } | null;
  warning?: string | null;
  error?: string;
};

export function normalizeMemberEmail(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().toLowerCase();
  return text || null;
}

export function isValidMemberEmail(value: unknown): boolean {
  const email = normalizeMemberEmail(value);
  if (!email || email.length > 255) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateMemberDisplayName(raw: unknown): string | null {
  const name = String(raw ?? "").trim();
  if (!name) return "Full name is required.";
  if (name.length > 255) return "Full name must be at most 255 characters.";
  return null;
}

export function validateMemberEmail(raw: unknown): string | null {
  const text = String(raw ?? "").trim();
  if (!text) return "Email is required.";
  if (!isValidMemberEmail(text)) return "Enter a valid email address.";
  return null;
}

export function memberRoleLabel(roleName: string | null | undefined): string {
  if (roleName === "OHTAdmin") return "OHT Admin";
  if (roleName === "CommunityNavigator") return "Community Navigator";
  return roleName || "Role";
}

export function mapNotificationStatus(
  raw: { attempted?: boolean; sent?: boolean } | null | undefined
): SuperAdminOhtNotificationStatus {
  return {
    attempted: raw?.attempted === true,
    sent: raw?.sent === true,
  };
}

export function canResendOhtMemberAccessEmail(opts: {
  member: Pick<SuperAdminOhtMember, "isActive" | "userStatus">;
  ohtIsActive: boolean;
}): boolean {
  return (
    opts.member.isActive === true &&
    String(opts.member.userStatus || "").trim() === "Approved" &&
    opts.ohtIsActive === true
  );
}

/**
 * Transient provisioning/reactivation copy including notification attempt result.
 * Does not invent invitation language.
 */
export function memberProvisionSuccessMessage(result: {
  userCreated?: boolean;
  assignmentState?: string | null;
  alreadyAssigned?: boolean;
  notification?: SuperAdminOhtNotificationStatus | null;
  warning?: string | null;
  email?: string | null;
}): string {
  const state = String(result.assignmentState || "").trim();
  if (state === "already_active" || result.alreadyAssigned === true) {
    return "This user already has this role for this OHT.";
  }

  const notification = mapNotificationStatus(result.notification);
  const email = normalizeMemberEmail(result.email);
  const emailSuffix = email
    ? ` An access email was sent to ${email}.`
    : " An access email was sent.";

  if (state === "reactivated") {
    if (notification.attempted && !notification.sent) {
      return (
        result.warning?.trim() ||
        "Access was reactivated successfully, but the notification email could not be sent."
      );
    }
    if (notification.attempted && notification.sent) {
      return `Access reactivated successfully.${emailSuffix}`;
    }
    return "Access reactivated successfully.";
  }

  // created (and any other successful provision path)
  if (notification.attempted && !notification.sent) {
    return (
      result.warning?.trim() ||
      "Access was added successfully, but the notification email could not be sent."
    );
  }

  const base =
    result.userCreated === true
      ? "New user access created successfully."
      : "Access added successfully.";

  if (notification.attempted && notification.sent) {
    return `${base}${emailSuffix}`;
  }
  return base;
}

export function memberAccessResultTone(result: {
  assignmentState?: string | null;
  alreadyAssigned?: boolean;
  notification?: SuperAdminOhtNotificationStatus | null;
}): "success" | "warning" {
  const state = String(result.assignmentState || "").trim();
  if (state === "already_active" || result.alreadyAssigned === true) {
    return "success";
  }
  const notification = mapNotificationStatus(result.notification);
  if (notification.attempted && !notification.sent) return "warning";
  return "success";
}

function mapMember(raw: any): SuperAdminOhtMember {
  return {
    userRoleId: Number(raw?.userRoleId),
    userId: Number(raw?.userId),
    displayName: raw?.displayName == null ? null : String(raw.displayName),
    email: raw?.email == null ? null : String(raw.email),
    userStatus: raw?.userStatus == null ? null : String(raw.userStatus),
    roleName: raw?.roleName == null ? "" : String(raw.roleName),
    isActive: raw?.isActive === true || raw?.isActive === 1,
    activatedAt: raw?.activatedAt || null,
    deactivatedAt: raw?.deactivatedAt || null,
  };
}

export async function listSuperAdminOhtMembers(
  ohtId: string | number,
  signal?: AbortSignal
): Promise<SuperAdminOhtMember[]> {
  const id = encodeURIComponent(String(ohtId).trim());
  const res = await fetch(`/api/super-admin/ohts/${id}/members`, {
    method: "GET",
    credentials: "include",
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtMembersListResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to load members (${res.status})`);
  }

  return Array.isArray(body?.members)
    ? body!.members!.map(mapMember)
    : [];
}

export async function createSuperAdminOhtMember(
  ohtId: string | number,
  input: CreateSuperAdminOhtMemberInput,
  signal?: AbortSignal
): Promise<CreateSuperAdminOhtMemberResult> {
  const id = encodeURIComponent(String(ohtId).trim());
  const payload = {
    displayName: String(input.displayName ?? "").trim(),
    email: normalizeMemberEmail(input.email) || "",
    roleName: input.roleName,
  };

  const res = await fetch(`/api/super-admin/ohts/${id}/members`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });

  const body = (await parseJsonSafe(res)) as SuperAdminOhtMemberMutationResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to add member (${res.status})`);
  }

  if (!body?.member) {
    throw new SuperAdminOhtApiError(
      500,
      "Member provisioning response was incomplete.",
      body
    );
  }

  return {
    member: mapMember(body.member),
    userCreated: body.userCreated === true,
    assignmentState: body.assignmentState || "created",
    alreadyAssigned: body.alreadyAssigned === true,
    notification: mapNotificationStatus(body.notification),
    warning: body.warning == null ? null : String(body.warning),
  };
}

export async function deactivateSuperAdminOhtMember(
  ohtId: string | number,
  userRoleId: string | number,
  signal?: AbortSignal
): Promise<SuperAdminOhtMember> {
  const id = encodeURIComponent(String(ohtId).trim());
  const roleId = encodeURIComponent(String(userRoleId).trim());
  const res = await fetch(
    `/api/super-admin/ohts/${id}/members/${roleId}/deactivate`,
    {
      method: "POST",
      credentials: "include",
      signal,
    }
  );

  const body = (await parseJsonSafe(res)) as SuperAdminOhtMemberMutationResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to deactivate member (${res.status})`);
  }

  if (!body?.member) {
    throw new SuperAdminOhtApiError(
      500,
      "Deactivate member response was incomplete.",
      body
    );
  }

  return mapMember(body.member);
}

export async function reactivateSuperAdminOhtMember(
  ohtId: string | number,
  userRoleId: string | number,
  signal?: AbortSignal
): Promise<ReactivateSuperAdminOhtMemberResult> {
  const id = encodeURIComponent(String(ohtId).trim());
  const roleId = encodeURIComponent(String(userRoleId).trim());
  const res = await fetch(
    `/api/super-admin/ohts/${id}/members/${roleId}/reactivate`,
    {
      method: "POST",
      credentials: "include",
      signal,
    }
  );

  const body = (await parseJsonSafe(res)) as SuperAdminOhtMemberMutationResponse | null;

  if (!res.ok) {
    throwOhtApiError(res, body, `Failed to reactivate member (${res.status})`);
  }

  if (!body?.member) {
    throw new SuperAdminOhtApiError(
      500,
      "Reactivate member response was incomplete.",
      body
    );
  }

  return {
    member: mapMember(body.member),
    alreadyActive: body.alreadyActive === true,
    notification: mapNotificationStatus(body.notification),
    warning: body.warning == null ? null : String(body.warning),
  };
}

export async function resendSuperAdminOhtMemberAccessEmail(
  ohtId: string | number,
  userRoleId: string | number,
  signal?: AbortSignal
): Promise<ResendSuperAdminOhtMemberAccessEmailResult> {
  const id = encodeURIComponent(String(ohtId).trim());
  const roleId = encodeURIComponent(String(userRoleId).trim());
  const res = await fetch(
    `/api/super-admin/ohts/${id}/members/${roleId}/resend-access-email`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
      signal,
    }
  );

  const body = (await parseJsonSafe(res)) as SuperAdminOhtMemberMutationResponse | null;

  if (!res.ok) {
    throwOhtApiError(
      res,
      body,
      res.status === 502
        ? "The access email could not be sent. Please try again."
        : `Failed to resend access email (${res.status})`
    );
  }

  return {
    notification: mapNotificationStatus(
      body?.notification || { attempted: true, sent: true }
    ),
  };
}
